// src/pages/status-sheet.jsx
// The Status Sheet report — auto-generated from CRM data, filterable by agent,
// and printable (browser Print → Save as PDF).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, Eye, EyeOff, RotateCcw, ChevronUp, ChevronDown } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useStateValue } from "../context/StateProvider";
import { actionType } from "../context/reducer";
import { isSuperAdminOwner } from "../utils/brokerageSettings";
import ReportTable from "../components/status-sheet/ReportTable";
import SummaryStrip from "../components/status-sheet/SummaryStrip";
import SectionHeader from "../components/status-sheet/SectionHeader";
import PropertyCard from "../components/status-sheet/PropertyCard";
import {
  normalizeLayout,
  moveSection,
  updateSection,
  resetLayout,
  isDefaultLayout,
} from "../utils/statusSheetLayout";
import { updateItem } from "../utils/firebasefunctions";
import {
  getClosings,
  getListings,
  getAppointments,
  getLeadsByTier,
  parseLocalDate,
  boardAddressKeys,
} from "../utils/statusSheetData";
import { creditedAgentId } from "../utils/dealCredit";
import {
  getContactsByIds,
  updateDealById,
  updateListingLead,
  getAlldealforAdmin,
  getListingAppointments,
  listenListingLeads,
  listenBuyerLeads,
} from "../utils/firebasefunctions";
import { normalizeContact } from "../utils/contactNormalize";
import { looksLikeJunk } from "../utils/junkFilter";
import { toast } from "react-toastify";

const usd = (n) => (n ? `$${Number(n).toLocaleString("en-US")}` : "");

// Inline quick-note for a sheet row. Saves on blur to the deal's statusNotes
// (its own field — independent of the detail page's Notes). Staff can type
// straight on the report and it sticks.
function NoteCell({ row }) {
  const [val, setVal] = useState(row.sheetNote || "");
  const save = async () => {
    const v = val.trim();
    if (v === (row.sheetNote || "").trim()) return;
    try {
      if (row.dealId) {
        await updateDealById(String(row.dealId), { statusNotes: v });
      } else if (row.leadId) {
        const res = await updateListingLead(row.leadId, { statusNotes: v });
        if (!res?.success) throw new Error(res?.error || "Save failed");
      } else {
        return;
      }
      row.sheetNote = v; // keep the row in sync so we don't re-save
    } catch (e) {
      toast.error("Couldn't save the note. Try again.");
    }
  };
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      placeholder="Add note…"
      className="w-full bg-transparent border-b border-dashed border-slate-300 focus:border-slate-600 outline-none text-[12px] py-0.5 placeholder:text-slate-300 print:border-0 print:placeholder:text-transparent"
    />
  );
}
const fmtDate = (s) => {
  // parseLocalDate keeps calendar dates in Central time (a plain new Date()
  // would read "2026-06-10" as UTC and show June 9).
  const d = parseLocalDate(s);
  return !d
    ? s || ""
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

// Listing status → pill tint (PropertyCard PILL keys).
const STAGE_TINT = {
  "pre-market": "emerald",
  "coming soon": "sky",
  active: "blue",
  "on hold": "slate",
};
const tintFor = (stageKey) =>
  STAGE_TINT[String(stageKey || "").replace(/-\d{4}$/, "")] || "slate";

const monthVolume = (rows) =>
  rows.reduce((sum, r) => sum + (Number(r.salePrice) || 0), 0);

// Known dev/test accounts (Humza's) — hidden from the Status Sheet everywhere,
// so old/test data is never used in any section. This HIDES them from the
// report only; it does not delete anything from the database.
const TEST_ACCOUNT_IDS = [
  "07GXeA7MIZS2chN5oijih2qOJiI3", // humza agent 2
  "DRwIpzUuHMP8b1f3LyQVCkDyh832", // humza samiullah
];
const looksLikeTest = (s) => String(s || "").toLowerCase().includes("humza");

export default function StatusSheet() {
  const navigate = useNavigate();
  const [
    {
      deals,
      listingLeads,
      buyerLeads,
      listingAppointments,
      employees,
      users,
      user,
    },
    dispatch,
  ] = useStateValue();

  // Agents (the "Employee" role) are locked to their OWN sheet — no picker, no
  // other agents, every section scoped to them. Admins / Supervisors /
  // Accountants keep the full agent picker.
  const restrictedToSelf = user?.role === "Employee" && !isSuperAdminOwner(user);
  const myName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
    user?.name ||
    user?.email ||
    "My Sheet";
  // The sheet must never print stale data: re-pull deals + appointments on every
  // open, and subscribe to the pipeline leads (their listeners normally only run
  // on the pipeline board pages — without this, opening the sheet directly on a
  // fresh load left the lead rows and Buyers & Leads section empty).
  useEffect(() => {
    let cancelled = false;
    getAlldealforAdmin("deals")
      .then((rows) => {
        if (!cancelled) dispatch({ type: actionType.STORE_DEALS, deals: rows });
      })
      .catch(() => {});
    getListingAppointments()
      .then((rows) => {
        if (!cancelled)
          dispatch({ type: actionType.STORE_LISTING_APPOINTMENTS, payload: rows });
      })
      .catch(() => {});
    const unsubListing = listenListingLeads((rows) =>
      dispatch({ type: actionType.STORE_LISTING_LEADS, payload: rows }),
    );
    const unsubBuyer = listenBuyerLeads((rows) =>
      dispatch({ type: actionType.STORE_BUYER_LEADS, payload: rows }),
    );
    return () => {
      cancelled = true;
      if (typeof unsubListing === "function") unsubListing();
      if (typeof unsubBuyer === "function") unsubBuyer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the set of test-account ids (the hardcoded ones + anyone whose name
  // or email contains "humza"), then drop their data up front so EVERY section
  // — dropdown, pending deals, listings — is clean.
  const testAgentIds = useMemo(() => {
    const pool = (users && users.length ? users : employees) || [];
    const ids = new Set(TEST_ACCOUNT_IDS);
    pool.forEach((u) => {
      const name = `${u.firstname || ""} ${u.lastname || ""} ${u.name || ""} ${u.email || ""}`;
      if (u.id && looksLikeTest(name)) ids.add(u.id);
    });
    return ids;
  }, [users, employees]);
  const cleanDeals = useMemo(
    () =>
      (deals || []).filter(
        (d) =>
          !testAgentIds.has(d.agentData) &&
          !looksLikeTest(d.assignedAgentName) &&
          !looksLikeJunk(d.propertyaddress),
      ),
    [deals, testAgentIds],
  );
  const cleanListings = useMemo(
    () =>
      (listingLeads || []).filter(
        (l) =>
          !testAgentIds.has(l.assignedAgent) &&
          !looksLikeTest(l.assignedAgentName) &&
          !looksLikeJunk(l.propertyAddress),
      ),
    [listingLeads, testAgentIds],
  );

  // The Agent filter lists only real producing agents + team leads — the people
  // actually assigned to a (non-deleted, non-test) deal or listing.
  const team = useMemo(() => {
    const live = (x) => !x?.deletedAt && x?.deleted !== true;
    const activeIds = new Set();
    cleanDeals.forEach((d) => {
      if (!live(d)) return;
      // Ask the SAME question the sections ask: who is credited with this
      // deal? creditedAgentId already handles the in-house referral (the unit
      // moves to the team lead) and the no-agent fallback. Reading the raw
      // agent field here instead would offer an agent whose rows no section
      // ever shows, and leave out the team lead whose rows they do.
      const owner = creditedAgentId(d);
      if (owner) activeIds.add(owner);
    });
    cleanListings.forEach((l) => {
      if (l.assignedAgent && live(l)) activeIds.add(l.assignedAgent);
    });
    const pool = (users && users.length ? users : employees) || [];
    return pool
      .filter((u) => u.id && activeIds.has(u.id))
      .map((u) => ({
        id: u.id,
        name:
          [u.firstname, u.lastname].filter(Boolean).join(" ") ||
          u.name ||
          u.email ||
          "Agent",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cleanDeals, cleanListings, users, employees]);

  // A deal's `customers` are contact IDs (older deals kept names inline). Resolve
  // the IDs to client names so the Client column isn't blank.
  const [clientById, setClientById] = useState({});
  useEffect(() => {
    const ids = new Set();
    cleanDeals.forEach((d) => {
      if (Array.isArray(d.customers)) {
        d.customers.forEach((c) => {
          if (typeof c === "string" && c.trim()) ids.add(c.trim());
          else if (c && c.id) ids.add(String(c.id).trim());
        });
      }
    });
    const list = [...ids];
    if (!list.length) {
      setClientById({});
      return;
    }
    let cancelled = false;
    getContactsByIds(list)
      .then((rows) => {
        if (cancelled) return;
        const map = {};
        (rows || []).forEach((c) => {
          const nm = c && c.id ? normalizeContact(c)?.name || "" : "";
          if (nm) map[c.id] = nm;
        });
        setClientById(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cleanDeals]);

  const [picked, setPicked] = useState("all"); // manager's choice: "all" | userId

  // Print layout — the order, visibility and headings of the sheet's sections.
  // The layout FOLLOWS THE PICKED AGENT: viewing Henry's sheet means Henry's
  // saved layout, so Client Care prints it exactly his way (2026-08-17 spec).
  // Edits save back to the picked agent's record — deliberate: Henry wants
  // Client Care able to tweak his layout for him.
  const pool = (users && users.length ? users : employees) || [];
  const layoutOwnerId =
    restrictedToSelf || picked === "all" ? user?.id : picked;
  const ownerIsViewer = layoutOwnerId === user?.id;
  const ownerRecord = ownerIsViewer
    ? user
    : pool.find((u) => u.id === layoutOwnerId);
  const ownerName = ownerIsViewer
    ? myName
    : team.find((u) => u.id === layoutOwnerId)?.name || "this agent";

  // Local edits per owner, so switching agents back and forth never shows a
  // stale pre-edit layout (the `users` pool doesn't live-refresh).
  const [layoutEdits, setLayoutEdits] = useState({});
  const sectionLayout =
    (layoutOwnerId && layoutEdits[layoutOwnerId]) ||
    normalizeLayout(ownerRecord?.statusSheetLayout);
  const [layoutOpen, setLayoutOpen] = useState(false);

  // Keystrokes stage locally; discrete clicks (and blur) also save — there
  // is no Save button to forget.
  const stageLayout = (next) => {
    if (layoutOwnerId) setLayoutEdits((p) => ({ ...p, [layoutOwnerId]: next }));
  };
  const applyLayout = (next) => {
    stageLayout(next);
    if (layoutOwnerId) {
      updateItem("users", String(layoutOwnerId), { statusSheetLayout: next })
        .then((res) => {
          if (res !== "success") toast.error("Couldn't save your print layout.");
        })
        .catch(() => toast.error("Couldn't save your print layout."));
    }
  };

  const titleFor = (key) =>
    sectionLayout.find((s) => s.key === key)?.label || key;
  // A locked agent always resolves to their own id, whatever `picked` holds.
  const agentId = restrictedToSelf ? user?.id || "__none__" : picked;

  // Addresses already on the board (deals + displayed listing leads), built
  // UNFILTERED by agent — a lead is "converted" if ANYONE's listing/deal exists
  // for its property. Drives the display dedup in appointments + lead tiers.
  const boardKeys = useMemo(
    () => boardAddressKeys(cleanDeals, cleanListings),
    [cleanDeals, cleanListings],
  );

  const filter = {
    agentId: agentId === "all" ? null : agentId,
    clientById,
    boardKeys,
  };

  const closings = useMemo(
    () => getClosings(cleanDeals, filter),
    [cleanDeals, agentId, clientById],
  );
  const listings = useMemo(
    () => getListings(cleanListings, cleanDeals, filter),
    [cleanListings, cleanDeals, agentId, clientById],
  );
  // Route listing groups to their print-layout sections (Active / Coming
  // Soon / Pre-Market this year / next year / On Hold are independently
  // arrangeable). Anything unmapped rides in "active" — statusSheetData
  // already defaults `section` to it.
  const listingSectionKeys = ["active", "coming-soon", "premarket", "premarket-next", "on-hold"];
  const listingsBySection = useMemo(() => {
    const by = { active: [], "coming-soon": [], premarket: [], "premarket-next": [], "on-hold": [] };
    listings.forEach((g) => (by[g.section] || by.active).push(g));
    return by;
  }, [listings]);
  const thisYear = new Date().getFullYear();
  const appts = useMemo(
    () =>
      getAppointments(
        (listingAppointments || []).filter(
          (a) => !looksLikeJunk(a.address || a.propertyAddress || a.name),
        ),
        filter,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listingAppointments, agentId, boardKeys],
  );
  const leads = useMemo(
    () => getLeadsByTier(buyerLeads, cleanListings, filter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buyerLeads, cleanListings, agentId, boardKeys],
  );

  const counts = {
    pending: closings.reduce((n, g) => n + g.rows.length, 0),
    listings: listings.reduce((n, g) => n + g.rows.length, 0),
    appointments: appts.open.length + appts.nextYear.length,
    aBuyers: leads.A.length,
  };

  const today = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <PageHeader title="Status Sheet" />
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        {/* Controls — hidden when printing */}
        <div className="flex items-center justify-between mb-5 print:hidden">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500">Agent</span>
            {restrictedToSelf ? (
              <span className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-sm text-slate-700">
                {myName}
              </span>
            ) : (
              <select
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="all">All Agents</option>
                {team.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLayoutOpen((v) => !v)}
              className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-slate-50"
            >
              Print layout
              {layoutOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 bg-[#1E293B] text-white rounded-lg px-4 py-2 text-sm font-semibold"
            >
              <Printer size={16} /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Whose layout is driving the sheet — only shown when it isn't yours. */}
        {!ownerIsViewer && (
          <div className="mb-5 print:hidden flex items-center gap-2 bg-sky-50 border border-sky-200 text-sky-900 rounded-lg px-3 py-2 text-[12.5px]">
            Using {ownerName}'s saved layout. Print layout changes save to their login.
          </div>
        )}

        {/* Print layout — reorder, hide, retitle and page-break the sections.
           Never printed; it only shapes what is. */}
        {layoutOpen && (
          <div className="mb-5 border border-slate-200 rounded-xl bg-white print:hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-800">Print layout</div>
                <div className="text-[11.5px] text-slate-500">
                  Order, headings and what prints. Saved to {ownerIsViewer ? "your" : `${ownerName}'s`} login.
                </div>
              </div>
              {!isDefaultLayout(sectionLayout) && (
                <button
                  onClick={() => applyLayout(resetLayout())}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-600 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50"
                >
                  <RotateCcw size={13} /> Reset
                </button>
              )}
            </div>
            {sectionLayout.map((sec, i) => (
              <div
                key={sec.key}
                className="px-4 py-2.5 border-b border-slate-100 last:border-0 flex flex-wrap items-center gap-x-3 gap-y-2"
              >
                <div className="flex flex-col">
                  <button
                    aria-label={`Move ${sec.label} up`}
                    disabled={i === 0}
                    onClick={() => applyLayout(moveSection(sectionLayout, sec.key, -1))}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    aria-label={`Move ${sec.label} down`}
                    disabled={i === sectionLayout.length - 1}
                    onClick={() => applyLayout(moveSection(sectionLayout, sec.key, 1))}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
                <input
                  value={sec.label}
                  onChange={(e) =>
                    stageLayout(
                      updateSection(sectionLayout, sec.key, { label: e.target.value }),
                    )
                  }
                  onBlur={() => applyLayout(sectionLayout)}
                  className={`flex-1 min-w-[10rem] text-sm bg-slate-50 border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-[#1E293B] ${sec.hidden ? "text-slate-400 line-through" : "text-slate-800"}`}
                />
                <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={sec.pageBreak}
                    onChange={(e) =>
                      applyLayout(
                        updateSection(sectionLayout, sec.key, { pageBreak: e.target.checked }),
                      )
                    }
                  />
                  New page
                </label>
                <button
                  aria-label={sec.hidden ? `Include ${sec.label}` : `Leave out ${sec.label}`}
                  onClick={() =>
                    applyLayout(updateSection(sectionLayout, sec.key, { hidden: !sec.hidden }))
                  }
                  className="text-slate-500 hover:text-slate-800"
                >
                  {sec.hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Printable report */}
        <div id="status-sheet-print">
          <div className="mb-5">
            <h1 className="text-xl font-bold text-slate-900">
              Closing &amp; Active Listing Status Sheet
            </h1>
            <p className="text-xs text-slate-500">
              {today}
              {agentId === "all"
                ? " · all agents"
                : ` · ${
                    restrictedToSelf
                      ? myName
                      : team.find((u) => u.id === agentId)?.name || "one agent"
                  }`}
            </p>
          </div>

          <SummaryStrip
            pending={counts.pending}
            listings={counts.listings}
            appointments={counts.appointments}
            aBuyers={counts.aBuyers}
          />

          {/* Sections print in the order set in the Print layout panel. Each
             block is keyed so the layout can reorder, hide and retitle it. */}
          {sectionLayout.filter((s) => !s.hidden).map((s) => (
            <div key={s.key} className={s.pageBreak ? "break-before-page" : ""}>
              {s.key === "pending" && (<>
              {/* A — Pending Deals: one box per deal, grouped by closing month */}
              {closings.map((g) => (
                <section key={g.month} className="mb-5 break-inside-avoid-page">
                  <SectionHeader
                    color="blue"
                    title={titleFor("pending")}
                    subtitle={`${g.month} · ${g.rows.length} ${g.rows.length === 1 ? "Deal" : "Deals"} · ${usd(monthVolume(g.rows))}`}
                  />
                  {g.rows.map((r) => (
                    <PropertyCard
                      key={r.dealId}
                      heading={r.address}
                      onOpen={r.dealId ? () => navigate(`/dashboard/deals-detail/${r.dealId}`) : undefined}
                      pill={{ label: r.sb === "B" ? "Buyer" : "Seller", tint: r.sb === "B" ? "sky" : "blue" }}
                      meta={[fmtDate(r.closingDate), r.client]}
                      right={usd(r.salePrice)}
                      note={<NoteCell row={r} />}
                    />
                  ))}
                </section>
              ))}
              {closings.length === 0 && (
                <section className="mb-5">
                  <SectionHeader color="blue" title={titleFor("pending")} />
                  <p className="text-xs text-slate-400">No pending deals.</p>
                </section>
              )}

              </>)}
              {listingSectionKeys.includes(s.key) && (<>
              {/* B — Listings: five independent sections (Active / Coming
                 Soon / Pre-Market this year / next year / On Hold), one box
                 per listing. Odd statuses print inside Active under their
                 own heading so nothing vanishes. */}
              {listingsBySection[s.key].map((g) => (
                <section key={g.stageKey} className="mb-5 break-inside-avoid-page">
                  <SectionHeader
                    color="green"
                    title={
                      s.key === "active" && g.stageKey !== "active"
                        ? g.status
                        : titleFor(s.key)
                    }
                    subtitle={
                      s.key === "premarket"
                        ? String(thisYear)
                        : s.key === "premarket-next" && g.year
                          ? String(g.year)
                          : undefined
                    }
                    count={g.rows.length}
                  />
                  {g.rows.map((r) => (
                    <PropertyCard
                      key={r.dealId || r.leadId}
                      heading={r.address}
                      onOpen={r.dealId ? () => navigate(`/dashboard/deals-detail/${r.dealId}`) : undefined}
                      pill={{ label: g.status, tint: tintFor(g.stageKey) }}
                      meta={[r.client, r.assigned, r.exp ? `Exp ${fmtDate(r.exp)}` : ""]}
                      right={usd(r.price)}
                      note={<NoteCell row={r} />}
                    />
                  ))}
                </section>
              ))}
              {listingsBySection[s.key].length === 0 && (
                <section className="mb-5">
                  <SectionHeader
                    color="green"
                    title={titleFor(s.key)}
                    subtitle={
                      s.key === "premarket"
                        ? String(thisYear)
                        : s.key === "premarket-next"
                          ? String(thisYear + 1)
                          : undefined
                    }
                  />
                  <p className="text-xs text-slate-400">None.</p>
                </section>
              )}

              </>)}
              {s.key === "appointments" && (<>
              {/* D — Listing Appointments */}
              <section className="mb-5 break-inside-avoid-page">
                <SectionHeader
                  color="amber"
                  title={titleFor("appointments")}
                  subtitle="Unconverted Leads"
                  count={appts.open.length}
                />
                {appts.open.length === 0 && <p className="text-xs text-slate-400">No open appointments.</p>}
                {appts.open.map((r, i) => (
                  <PropertyCard
                    key={r.dealId || i}
                    heading={r.address}
                    onOpen={r.dealId ? () => navigate(`/dashboard/deals-detail/${r.dealId}`) : undefined}
                    meta={[fmtDate(r.date), r.client, r.phone]}
                  />
                ))}
              </section>
              {appts.nextYear.length > 0 && (
                <section className="mb-5 break-inside-avoid-page">
                  <SectionHeader color="amber" title={titleFor("appointments")} subtitle="Next Year" count={appts.nextYear.length} />
                  {appts.nextYear.map((r, i) => (
                    <PropertyCard
                      key={r.dealId || i}
                      heading={r.address}
                      onOpen={r.dealId ? () => navigate(`/dashboard/deals-detail/${r.dealId}`) : undefined}
                      meta={[fmtDate(r.date), r.client, r.phone]}
                    />
                  ))}
                </section>
              )}

              </>)}
              {s.key === "leads" && (<>
              {/* E — Buyers & Leads (people, not properties — stays a compact list) */}
              <SectionHeader color="purple" title={titleFor("leads")} count={leads.A.length + leads.B.length + leads.C.length + leads.ungraded.length} />
              {["A", "B", "C", "ungraded"].map((tier) => (
                <ReportTable
                  key={tier}
                  title={
                    tier === "ungraded" ? "Leads — Unrated" : `Buyers & Leads — ${tier}`
                  }
                  columns={[
                    { key: "name", label: "Client", width: "48%" },
                    { key: "stage", label: "Stage", width: "52%" },
                  ]}
                  rows={leads[tier]}
                  empty="None."
                />
              ))}
              </>)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
